import { cn } from "@/lib/utils";
import {
  attemptChunkLoadRecovery,
  buildRuntimeErrorDiagnostics,
  CHUNK_RELOAD_IN_PROGRESS_MESSAGE,
  CHUNK_RELOAD_MANUAL_MESSAGE,
  CHUNK_RELOAD_USER_MESSAGE,
  createIncidentId,
  hasAttemptedChunkReload,
  isChunkLoadError,
  logRuntimeErrorDiagnostics,
  markChunkReloadAttempted,
  type RuntimeErrorDiagnostics,
} from "@/lib/chunkLoadRecovery";
import { isCustomerFacingPublicPath } from "@shared/staffForegroundIssue";
import { getClientBuildCommit } from "@/lib/buildIdentity";
import { AlertTriangle, RotateCcw } from "lucide-react";
import { Component, ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isChunkLoadFailure: boolean;
  incidentId: string | null;
  diagnostics: RuntimeErrorDiagnostics | null;
  autoReloadTriggered: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      isChunkLoadFailure: false,
      incidentId: null,
      diagnostics: null,
      autoReloadTriggered: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const incidentId = createIncidentId();
    return {
      hasError: true,
      error,
      isChunkLoadFailure: isChunkLoadError(error),
      incidentId,
      autoReloadTriggered: false,
    };
  }

  componentDidCatch(error: Error) {
    const diagnostics = buildRuntimeErrorDiagnostics(error, {
      incidentId: this.state.incidentId ?? undefined,
      serverBuildId: readCachedServerBuildId(),
    });
    logRuntimeErrorDiagnostics(diagnostics);
    this.setState({ diagnostics, isChunkLoadFailure: diagnostics.isChunkLoadFailure });

    const shouldTreatAsDeployMismatch =
      diagnostics.isChunkLoadFailure || diagnostics.buildMismatch;

    if (!shouldTreatAsDeployMismatch) return;

    if (attemptChunkLoadRecovery(error)) {
      this.setState({ autoReloadTriggered: true, isChunkLoadFailure: true });
      return;
    }

    // Client/server build mismatch without a classic chunk message — still one-shot reload.
    if (diagnostics.buildMismatch && !hasAttemptedChunkReload()) {
      markChunkReloadAttempted();
      this.setState({ autoReloadTriggered: true, isChunkLoadFailure: true });
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      const isChunkFailure =
        this.state.isChunkLoadFailure ||
        isChunkLoadError(this.state.error) ||
        Boolean(this.state.diagnostics?.buildMismatch);
      const incidentId = this.state.incidentId ?? this.state.diagnostics?.incidentId ?? "UNKNOWN";
      const alreadyAttempted = hasAttemptedChunkReload() && !this.state.autoReloadTriggered;

      if (isChunkFailure) {
        return (
          <div className="flex items-center justify-center min-h-screen p-8 bg-background">
            <div className="flex flex-col items-center w-full max-w-md p-8 text-center">
              <AlertTriangle size={48} className="text-primary mb-6 flex-shrink-0" />
              <h2 className="text-xl mb-3 font-medium">{CHUNK_RELOAD_USER_MESSAGE}</h2>
              <p className="text-sm text-muted-foreground mb-4">
                {this.state.autoReloadTriggered || !alreadyAttempted
                  ? CHUNK_RELOAD_IN_PROGRESS_MESSAGE
                  : CHUNK_RELOAD_MANUAL_MESSAGE}
              </p>
              <p className="text-xs text-muted-foreground mb-6 font-mono">Incident ID: {incidentId}</p>
              {alreadyAttempted ? (
                <button
                  onClick={() => window.location.reload()}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg",
                    "bg-primary text-primary-foreground",
                    "hover:opacity-90 cursor-pointer",
                  )}
                >
                  <RotateCcw size={16} />
                  Reload
                </button>
              ) : null}
            </div>
          </div>
        );
      }

      const name = this.state.error?.name || this.state.diagnostics?.errorName || "Error";
      const message =
        this.state.error?.message ||
        this.state.diagnostics?.message ||
        "(no message)";
      const stack = this.state.error?.stack || this.state.diagnostics?.stack || "";

      const publicSurface =
        typeof window !== "undefined" && isCustomerFacingPublicPath(window.location.pathname);

      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div className="flex flex-col items-center w-full max-w-2xl p-8">
            <AlertTriangle
              size={48}
              className="text-destructive mb-6 flex-shrink-0"
            />

            <h2 className="text-xl mb-4">An unexpected error occurred.</h2>

            {publicSurface ? (
              <p className="mb-6 text-sm text-muted-foreground">Please try again. If the problem continues, contact Clarity.</p>
            ) : (
            <div className="p-4 w-full rounded bg-muted overflow-auto mb-6 text-left space-y-2">
              <p className="text-sm font-mono text-foreground">Incident ID: {incidentId}</p>
              <p className="text-sm text-foreground">
                <span className="font-medium">{name}</span>
                {message ? `: ${message}` : ""}
              </p>
              {stack ? (
                <pre className="text-sm text-muted-foreground whitespace-break-spaces">{stack}</pre>
              ) : (
                <p className="text-sm text-muted-foreground">No stack trace available.</p>
              )}
              <p className="text-xs text-muted-foreground font-mono">
                Client build: {getClientBuildCommit()}
              </p>
            </div>
            )}

            <button
              onClick={() => window.location.reload()}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg",
                "bg-primary text-primary-foreground",
                "hover:opacity-90 cursor-pointer"
              )}
            >
              <RotateCcw size={16} />
              Reload Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const SERVER_BUILD_CACHE_KEY = "clarity-portal:server-build-commit";

function readCachedServerBuildId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(SERVER_BUILD_CACHE_KEY);
  } catch {
    return null;
  }
}

export function cacheServerBuildId(commit: string | null | undefined): void {
  if (typeof window === "undefined") return;
  if (!commit) return;
  try {
    window.sessionStorage.setItem(SERVER_BUILD_CACHE_KEY, commit);
  } catch {
    /* ignore */
  }
}

export default ErrorBoundary;
